import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AccountDashboardWidgetBase from "./AccountDashboardWidgetBase";
import { Routes } from '../../../routes/routes';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import { ReduxState } from '../../../store/types';
import SwitchWithLabel from '../../../components/SwitchWithLabel/SwitchWithLabel';
import { generateColumns, useStyles } from './AccountDashboardCallForwardingWidget.utils';
import { ReactComponent as CallForwarding } from '../../../assets/accountDashboard/CallForwarding.svg';
import { getServiceFeatureLockedStatus, getServiceFeatureValue } from '../../../utils/extensions/getServiceValue';
import { ForwardModeFlag, ServiceFeatureName } from '../../../store/types/ServiceFeature';
import CustomizedTooltip from '../../../components/Tooltip/Tooltip';
import {Formik} from 'formik';
import classNames from 'classnames';
import CreationButton from '../../../components/CreationButton/CreationButton';
import SelectField from '../../../components/SelectField/SelectField';
import { FollowMeSequence, TransportProtocol } from '../../../store/types/AccountFollowMeSettings';
import { countRulesWeights, redistributeFullValuesToRulesWith } from '../../../components/Forms/Extensions/CallForwarding/FollowMeRulesList.utils';
import { CallForwardingRule, callForwardingRuleEmptyRule } from '../../../components/Forms/Extensions/CallForwarding/CallForwardingForm.utils';
import { PaginationMode } from '../../../components/DataGrid/types';
import ForwardingRuleDialog from '../../../components/Forms/Extensions/CallForwarding/ForwardingRuleDialog/ForwardingRuleDialog';
import AlertDialog from '../../../components/AlertDialog/AlertDialog';
import { DialogButton } from '../../../components/AlertDialog/DialogContainer';
import DataGrid from '../../../components/DataGrid/DataGrid';
import {v4 as id} from 'uuid';
import { actions } from '../../../store';
import { convertArrayOfDaysStringToDaysSelectItems, convertArrayOfMonthsStringToMonthSelectItems, convertArrayOfNumbersToRangesString, convertArrayOfYearsStringToYearsSelectItems } from '../../../utils/extensions/RingScheduleSummary';
import { IntervalStatus } from '../../../components/IntervalSelect/IntervalSelect.utils';
import AccountDashboardLoader from './AccountDashboardLoader';
import { CallForwardingFormType } from '../../../store/actions/accountDashboard/payloads';
import FollowRuleForm from '../../../components/Forms/Extensions/CallForwarding/FollowRuleForm';

const AccountDashboardCallForwardingWidget: React.VFC = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {isLoadingAccountDetails, isLoadingServiceFeatures, accountServiceFeatures} = useSelector((state: ReduxState) => state.accountControlPanel);
    const {isUpdatingCallForwardingSettings} = useSelector((state: ReduxState) => state.accountDashboard);
    const {accountFollowMeSettings} = useSelector(
        (state: ReduxState) => state.extensions,
    );
    const i_account = useSelector((state: ReduxState) => state.accountControlPanel?.accountDetails?.i_account);

    const permittedSipProxies = useSelector<ReduxState, string[]>(
        (state) => state.extensions.permittedSipProxies || [],
    );

    const [
        isCallForwardingRuleDialogVisible,
        setIsCallForwardingRuleDialogVisible,
    ] = useState(false);
    const [isRemoveRuleDialogVisible, setIsRemoveRuleDialogVisible] = useState(
        false,
    );

    const [ruleToEdit, setRuleToEdit] = useState<CallForwardingRule | undefined>(undefined);
    const [ruleToEditIndex, setRuleToEditIndex] = useState<number | undefined>(
        undefined,
    );
    
    const isDataLoading = useSelector(
        (state: ReduxState) =>
            state.extensions.extensionDetailsTabs?.callForwardingTab
                ?.isLoading || false,
    );

    const isLoading = useMemo(() => {
        return isDataLoading || isLoadingAccountDetails || isUpdatingCallForwardingSettings || isLoadingServiceFeatures;
    }, [isDataLoading, isLoadingAccountDetails, isUpdatingCallForwardingSettings, isLoadingServiceFeatures]);

    const initialValues = useMemo(() => {
        const method: ForwardModeFlag = getServiceFeatureValue(
            ServiceFeatureName.ForwardMode,
            accountServiceFeatures,
        ) || ForwardModeFlag.NoForwarding;

        const rules = accountFollowMeSettings?.followme_numbers?.map(
            (v) =>
                ({
                    id: v.i_follow_me_number,
                    description: v.name ?? '',
                    number: v.redirect_number || '',
                    ringFor: v.timeout || '15',
                    enabled: v.active === 'Y',
                    weight: v.weight.toString(),
                    intervals: {
                        activity: !v.period_definition
                            ? IntervalStatus.Always
                            : IntervalStatus.OnlyFollowingTimeInterval,
                        intervals:
                            v.period_definition?.map((interval) => ({
                                wholeDay: !interval.start_time,
                                startTime: interval.start_time?.slice(
                                    0,
                                    5,
                                ),
                                endTime: interval.end_time?.slice(0, 5),
                                daysOfMonth: convertArrayOfNumbersToRangesString(
                                    interval.monthday_list,
                                ),
                                days: convertArrayOfDaysStringToDaysSelectItems(
                                    interval.weekday_list,
                                ),
                                months: convertArrayOfMonthsStringToMonthSelectItems(
                                    interval.month_list,
                                ),
                                years: convertArrayOfYearsStringToYearsSelectItems(
                                    interval.year_list,
                                ),
                            })) || []
                    },
                    callingPartyDisplay: v.keep_original_cli,
                    transportProtocol:
                        v.use_tcp === 'N'
                            ? TransportProtocol.UDP
                            : TransportProtocol.TCP,
                    keepOriginalCld: v.keep_original_cld === 'Y',
                    sipProxy: v.domain ?? undefined,
                }),
        ) || [];

        const locked = getServiceFeatureLockedStatus(
                ServiceFeatureName.ForwardMode,
                accountServiceFeatures,
            ) || false;

        return {
            setDefaultFollowMeSequence: !accountFollowMeSettings?.followme_info.sequence,
            callForwardingStatus: method != ForwardModeFlag.NoForwarding,
            callForwardingMethod: method,
            locked: locked,
            callForwardingRuleOrder: 
                accountFollowMeSettings?.followme_info.sequence ||
                FollowMeSequence.Simultaneous,
            callForwardingRules: rules?.length == 0 
                && (method == ForwardModeFlag.SimpleForwarding || method == ForwardModeFlag.ForwardToSIPUri) 
                    ? [callForwardingRuleEmptyRule] : rules,
            callForwardingDeletedRules: []
        } as CallForwardingFormType;
    }, [accountServiceFeatures, accountFollowMeSettings]);

    const forwardModesList = useMemo(
        () =>
            Object.values(ForwardModeFlag)
                .slice(2)
                .map((v) => ({
                    name: t(`enums:forwardModeEnum.${v}`),
                    value: v,
                })),
        [],
    );

    const followMeSequences = Object.values(FollowMeSequence).map((v) => ({
        name: t(`enums:followMeSequences.${v}`),
        value: v,
    }));

    const fetchData = useCallback(() => {
        if(i_account) {
            dispatch(actions.getAccountDashboardServiceFeatures.request({i_account: i_account}));
            dispatch(actions.getCallForwardingTabData.request({i_account: i_account}));
        }
    }, [i_account]);

    const handleSubmitForm = useCallback((form: CallForwardingFormType) => {
        dispatch(actions.updateAccountDashboardCallForwardingSettings.request({
            initialValues: initialValues,
            changedValues: form,
            accountId: i_account ?? 0,
            callback: () => {
                fetchData();
            }
        }));
    }, [initialValues, i_account, fetchData]);

    useEffect(() => {
        fetchData();
    }, [i_account]);

    if(isLoading) {
        return (<AccountDashboardWidgetBase
            route={Routes.AccountExtension}
            icon={<CallForwarding />}
            label={t('screens:accountDashboard.callForwarding')}
        >
            <AccountDashboardLoader />
        </AccountDashboardWidgetBase>);
    }

    return (<AccountDashboardWidgetBase
        route={Routes.AccountExtension}
        icon={<CallForwarding />}
        label={t('screens:accountDashboard.callForwarding')}
    >
        <Formik<CallForwardingFormType>
            initialValues={initialValues}
            enableReinitialize={true}
            validateOnChange={false}
            onSubmit={handleSubmitForm}
        >
            {({dirty, values, setFieldValue, submitForm, errors, setFieldError}) => (<>
                <div className={classes.callForwardingSwitchContainer}>
                    <CustomizedTooltip
                        title={t('tooltips:incomings.locked')}
                        copy={false}
                        disableHoverListener={!values.locked}
                    >
                        <SwitchWithLabel
                            id={"switch_call_forwarding"}
                            label={values.callForwardingMethod == ForwardModeFlag.NoForwarding
                                ? t('screens:accountDashboard.notActive')
                                : forwardModesList?.find(e => e.value == values.callForwardingMethod)?.name ?? ''}
                            className={classes.switch}
                            customLabelClass={classes.ringGroupSwitchLabel}
                            value={values.callForwardingMethod != ForwardModeFlag.NoForwarding}
                            field={"switch_call_forwarding"}
                            setValue={(_, value) => {
                                setFieldValue('callForwardingStatus', value);
                                if(value) {
                                    setFieldValue('callForwardingMethod', ForwardModeFlag.FollowMe);
                                    setFieldValue('callForwardingRuleOrder', FollowMeSequence.Simultaneous);
                                    setFieldValue('callForwardingRules', []);
                                    setFieldValue('callForwardingDeletedRules', []);
                                } else {
                                    setFieldValue('callForwardingMethod', ForwardModeFlag.NoForwarding);
                                    submitForm();
                                }
                            }}
                            disabled={values.locked}
                        />
                    </CustomizedTooltip>
                </div>
                {(values.callForwardingMethod ===
                    ForwardModeFlag.ForwardToSIPUri ||
                    values.callForwardingMethod ===
                    ForwardModeFlag.SimpleForwarding) && (
                        <FollowRuleForm
                            setFieldError={(field, value) => {
                                setFieldError(
                                    `callForwardingRules.[0].${field}`,
                                    value,
                                );
                            }}
                            setFieldValue={(field, value) => {
                                setFieldValue(
                                    `callForwardingRules.[0].${field}`,
                                    value,
                                );
                            }}
                            rule={values.callForwardingRules[0]}
                            permittedSipProxies={permittedSipProxies || ['']}
                            errors={errors?.callForwardingRules?.[0]}
                            mode={values.callForwardingMethod}
                            ruleContainerClass={classes.ruleContainer}
                            rowClass={classes.rowClass}
                            secondRowClass={classes.secondRowClass}
                            timeBeforeSipProxy
                        />
                    )
                }
                {(values?.callForwardingMethod == ForwardModeFlag.FollowMe || values?.callForwardingMethod == ForwardModeFlag.AdvancedForwarding) && (
                    <div className={classes.callForwardingModeContainer}>
                        <SelectField
                            dataQa="calling-party-display-select"
                            label={t('screens:extensions.ruleOrder')}
                            items={followMeSequences}
                            value={followMeSequences.find(
                                (v) => v.value === values.callForwardingRuleOrder,
                            )}
                            getOptionLabel={(v: { name: string; value: string }) => v.name}
                            getOptionSelected={(v: { name: string; value: string }) =>
                                v.value === values.callForwardingRuleOrder
                            }
                            onChange={(_: unknown,
                                value: { value: FollowMeSequence },
                            ) => {
                                setFieldValue('callForwardingRuleOrder', value.value);

                                if (value.value === FollowMeSequence.PercentageList) {
                                    setFieldValue(
                                        'callForwardingRules',
                                        redistributeFullValuesToRulesWith(values.callForwardingRules),
                                    );
                                }
                            }}
                            disableClearable
                        />

                        {(values?.callForwardingMethod != ForwardModeFlag.FollowMe && values?.callForwardingMethod != ForwardModeFlag.AdvancedForwarding)
                        ? (<></>)
                        : !!values.callForwardingRules.length ? (
                            <DataGrid<CallForwardingRule>
                                classes={{tableContainer: classes.tableContainer}}
                                columns={generateColumns(
                                    values.callForwardingMethod,
                                    t,
                                    setFieldValue,
                                    (rule: CallForwardingRule, index: number) => {
                                        setRuleToEdit(rule);
                                        setRuleToEditIndex(index);
                                        setIsCallForwardingRuleDialogVisible(true);
                                    },
                                    (rule: CallForwardingRule, index: number) => {
                                        setRuleToEdit(rule);
                                        setRuleToEditIndex(index);
                                        setIsRemoveRuleDialogVisible(true);
                                    },
                                )}
                                data={values.callForwardingRules}
                                rowCount={values.callForwardingRules?.length || 0}
                                centeredRows
                                narrowRows
                                paginationMode={PaginationMode.Client}
                                hideHeader={false}
                                internalHeader={false}
                                hideFooter
                                hidePagination
                            />
                        ) : (
                            <div className={classes.emptyListContainer}>
                                {t('screens:extensions.noForwardingRules')}
                            </div>
                        )}

                        <ForwardingRuleDialog
                            isOpen={isCallForwardingRuleDialogVisible}
                            toggleVisibility={() =>
                                setIsCallForwardingRuleDialogVisible(
                                    !isCallForwardingRuleDialogVisible,
                                )
                            }
                            permittedSipProxies={permittedSipProxies}
                            mode={values.callForwardingMethod}
                            ruleToEdit={ruleToEdit}
                            onEditRule={(editedRule: CallForwardingRule) => {
                                const callForwardingRules = [...values.callForwardingRules];
                                if (ruleToEditIndex !== undefined && ruleToEdit) {
                                    callForwardingRules[ruleToEditIndex] = {
                                        ...ruleToEdit,
                                        ...editedRule,
                                    };
                                    setFieldValue('callForwardingRules', callForwardingRules);
                                    setIsCallForwardingRuleDialogVisible(false);
                                }
                            }}
                            onAddRule={(rule: CallForwardingRule) => {
                                const callForwardingRules = [
                                    ...values.callForwardingRules,
                                    {
                                        ...rule,
                                        weight: !values.callForwardingRules.length ? '100' : '0',
                                        id: `created_${id()}`,
                                    },
                                ];
                                setFieldValue('callForwardingRules', callForwardingRules);
                                setIsCallForwardingRuleDialogVisible(false);
                            }}
                        />

                        <AlertDialog
                            isOpen={isRemoveRuleDialogVisible}
                            description={t('screens:extensions.deleteCallForwardingRule', {
                                name: ruleToEdit?.description,
                            })}
                            contentClass="alert-content"
                            className={classes.removeDialogContainer}
                            hideHeader={true}
                            dialogActionsButtons={[
                                <DialogButton
                                    key="cancel"
                                    label={t('common:cancel')}
                                    onClick={() =>
                                        setIsRemoveRuleDialogVisible(
                                            !isRemoveRuleDialogVisible,
                                        )
                                    }
                                />,
                                <DialogButton
                                    key="exlude"
                                    label={t('common:delete')}
                                    onClick={() => {
                                        if (ruleToEditIndex !== undefined) {
                                            const weights = countRulesWeights(
                                                values.callForwardingRules,
                                                ruleToEditIndex,
                                                '0',
                                            ).filter((_, index) => index !== ruleToEditIndex);
                                            let afterRemove = values.callForwardingRules
                                                .filter((_, index) => {
                                                    if(index == ruleToEditIndex && !values.callForwardingRules[index].id.toString().includes('created'))
                                                    {
                                                        setFieldValue('callForwardingDeletedRules', [...values.callForwardingDeletedRules,values.callForwardingRules[index]]);
                                                    }
                                                    return index !== ruleToEditIndex
                                                })
                                            if(values.callForwardingRuleOrder === FollowMeSequence.PercentageList){
                                                afterRemove = afterRemove.map((v, index) => ({...v, weight: weights[index]}));
                                            }
                                            setFieldValue(
                                                'callForwardingRules',
                                                afterRemove
                                            );
                                            setIsRemoveRuleDialogVisible(false);
                                        }
                                    }}
                                />,
                            ]}
                            dataQa="remove-forwarding-rule-modal"
                            dataTestId="remove-forwarding-rule-modal"
                        />
                    </div>
                )}
                {values?.callForwardingMethod != ForwardModeFlag.NoForwarding && (
                    <div className={classes.callForwardingActionsContainer}>
                        
                        {(values?.callForwardingMethod == ForwardModeFlag.FollowMe || values?.callForwardingMethod == ForwardModeFlag.AdvancedForwarding)
                            ? (<CreationButton
                                title={t('screens:extensions.addNewRule')}
                                className={classes.primary}
                                onClick={() => {
                                    setRuleToEdit(undefined);
                                    setRuleToEditIndex(undefined);
                                    setIsCallForwardingRuleDialogVisible(true);
                                }}
                                withPlus
                            />) 
                            : (<div></div>)
                        }
                        <CreationButton
                            title={t('common:save')}
                            className={classNames(classes.primary, classes.primaryActivated, !dirty && classes.disabled)}
                            onClick={submitForm}
                            disabled={!dirty}
                            withPlus={false}
                        />
                    </div>
                )}
            </>)}
        </Formik>
    </AccountDashboardWidgetBase>
    );
};

export default AccountDashboardCallForwardingWidget;